/**
 * This monstrosity prefetches the page shallowly so it can assign correct
 * revalidation tags, then fetches the full page payload
 *
 */
import { APIPath } from '@/cms/api/constants';
import {
  getShouldRetainTopLevelFields,
  grabDataFromResponse,
} from '@/cms/api/utils';
import { Entry, EntryVersion, GetPageResult, SinglePage } from '@/cms/types';
import { isDev } from '@/constant/env';
import { Logger } from '@/lib/Logging';
import { ApiCallStrapiProps } from '@/utils/fetch';

import { collectBlockTagsForPageEntity, collectLayoutTags } from './blockTags';
import { collectEntityTags } from './entityTags';
import { performStrapiFetch } from './getPageFetch';
import { tags } from './tags';
import {
  recursivelyStripUnnecessaryFields,
  retainTopLevelFields,
} from './utils';
import {
  createMissingContentError,
  getMissingContentIssues,
  shouldCheckForEmptyBlocks,
  shouldValidateEntryContent,
} from './validateEntryContent';

const logger = new Logger('getPage.ts');

/** Paths where post-response tags are collectedTags via shallow prefetch. */
const DYNAMIC_TAG_API_PATHS: readonly APIPath[] = [
  '/pages',
  '/blogs',
  '/blog-topics',
];

/** Deep enough for header/footer refs + block relation documentIds. */
const pLevelForTags = 'deep,3';

interface GetPageProps {
  slug?: string;
  documentId?: string;
  apiPath: APIPath;
  status?: EntryVersion;
  tags?: string[];
  fetchOptions?: ApiCallStrapiProps['fetchOptions'];
  envBridgeOptions?: ApiCallStrapiProps['envBridgeOptions'];
}

type ShallowEntity = {
  documentId?: string;
  attributes?: Entry & { documentId?: string };
};

function resolveEntityDocumentId(entity: ShallowEntity): string | undefined {
  return entity.attributes?.documentId ?? entity.documentId;
}

function collectDynamicCacheTags(
  entity: ShallowEntity,
  apiPath: APIPath
): string[] {
  const attributes = entity.attributes;
  const collectedTags: string[] = [];
  const docId = resolveEntityDocumentId(entity);

  if (docId && apiPath === '/pages') {
    collectedTags.push(tags.pageByDocId(docId));
  }
  if (docId && apiPath === '/blog-topics') {
    collectedTags.push(tags.blogTopicByDocId(docId));
  }

  if (!attributes?.slug) return collectedTags;
  if (apiPath !== '/pages' && apiPath !== '/blogs') return collectedTags;

  collectedTags.push(
    ...collectBlockTagsForPageEntity(attributes.blocks, attributes.slug),
    ...collectEntityTags(attributes.blocks),
    ...collectLayoutTags(attributes)
  );

  return collectedTags;
}

export async function getPage<T extends Entry = Entry>({
  slug,
  documentId,
  apiPath,
  status,
  tags: baseTags,
  fetchOptions,
  envBridgeOptions,
}: GetPageProps): Promise<GetPageResult<T>> {
  if (!slug && !documentId) {
    throw new Error('slug or documentId is required');
  }

  const fetchBase = {
    apiPath,
    slug,
    documentId,
    status,
    fetchOptions,
    envBridgeOptions,
  };

  let response;

  if (DYNAMIC_TAG_API_PATHS.includes(apiPath)) {
    // 1) shallow — discover tags (cached under base tags only)
    const pageShallowCheck = await performStrapiFetch<T>({
      ...fetchBase,
      pLevel: pLevelForTags,
      tags: baseTags,
    });

    const shallowEntity = grabDataFromResponse(pageShallowCheck);

    if (!shallowEntity) {
      return { data: null, meta: pageShallowCheck?.meta ?? null };
    }

    const collectedTags = collectDynamicCacheTags(shallowEntity, apiPath);

    const finalTags = Array.from(
      new Set([...(baseTags ?? []), ...collectedTags])
    );

    // 2) deep — full payload, tagged for collection / layout / entity revalidation
    response = await performStrapiFetch<T>({
      ...fetchBase,
      pLevel: 'deep',
      tags: finalTags,
    });
  } else {
    response = await performStrapiFetch<T>({
      ...fetchBase,
      tags: baseTags,
    });
  }

  const meta = response?.meta ?? null;

  const entityData = grabDataFromResponse(response);

  if (!entityData) {
    return {
      data: null,
      meta,
    };
  }

  let dataResult =
    recursivelyStripUnnecessaryFields<SinglePage<T>['data']>(entityData);

  if (getShouldRetainTopLevelFields(apiPath)) {
    dataResult = retainTopLevelFields(dataResult, entityData, [
      'publishedAt',
      'updatedAt',
      'createdAt',
    ]);
  }

  const resolvedStatus: EntryVersion =
    status ?? (isDev ? 'draft' : 'published');

  if (resolvedStatus === 'published' && shouldValidateEntryContent(apiPath)) {
    const issues = getMissingContentIssues(dataResult.attributes);

    if (issues.length > 0) {
      logger.error({
        message: 'CMS returned an entry without required content',
        context: { apiPath, slug, documentId, issues },
      });

      if (shouldCheckForEmptyBlocks()) {
        throw createMissingContentError({
          apiPath,
          identifier: slug ?? documentId,
          issues,
        });
      }
    }
  }

  return {
    data: dataResult,
    meta,
  };
}
